import React, { useState, useEffect } from "react";
import Context from "../Context";

import { TransferReviewModal } from "./orders/orderInfo/components/TransferReviewModal";
import { BatchAnonymizeModal } from "./users/BatchAnonymizeModal";
import {
  Search,
  ArrowRightLeft,
  Shield,
  Activity,
  Users,
  ShoppingBag,
  ExternalLink,
  Ticket,
  Tv,
  ArrowRight,
  Code2,
  Clock,
  Sparkles,
  ShieldAlert,
  RotateCw,
} from "lucide-react";

export const Home = () => {
  const { profileState, navigate, callApi } = Context();
  const isDev = profileState?.role === "dev";

  // Dev-only States
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [devLogs, setDevLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [pendingAnonymizeCount, setPendingAnonymizeCount] = useState(0);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);

  const fetchPendingTransfers = async () => {
    try {
      const res = await callApi(
        "orders-getTransferRequests",
        { status: "pending", limit: 10 },
        { notShowLoading: true }
      );
      if (res?.status && Array.isArray(res.data)) {
        setPendingTransfers(res.data);
      }
    } catch (e) {
      console.warn("Home pending transfer fetch error:", e);
    }
  };

  const fetchDevLogs = async () => {
    try {
      setIsLoadingLogs(true);
      const res = await callApi(
        "logs-getAll",
        { onlyDev: true, limit: 6 },
        { notShowLoading: true }
      );
      if (Array.isArray(res)) {
        setDevLogs(res);
      }
    } catch (e) {
      console.warn("Home dev logs fetch error:", e);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const fetchPendingAnonymizeSummary = async () => {
    try {
      const res = await callApi(
        "users-getPendingAnonymizeSummary",
        {},
        { notShowLoading: true }
      );
      if (res?.status) {
        setPendingAnonymizeCount(res.pendingCount || 0);
      }
    } catch (e) {
      console.warn("Home pending anonymize fetch error:", e);
    }
  };

  const handleOpenBatchModal = () => {
    setIsBatchModalOpen(true);
  };

  useEffect(() => {
    if (!isDev) return;
    fetchPendingTransfers();
    fetchDevLogs();
    fetchPendingAnonymizeSummary();
    const interval = setInterval(() => {
      fetchPendingTransfers();
      fetchDevLogs();
      fetchPendingAnonymizeSummary();
    }, 5 * 60 * 1000); // Polling ทุก 5 นาทีเพื่อประหยัด Quota

    const handleVisibility = () => {
      if (!document.hidden) {
        fetchPendingTransfers();
        fetchDevLogs();
        fetchPendingAnonymizeSummary();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [isDev]);

  // Handle Quick Search
  const handleQuickSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;

    // ถ้าเป็นเบอร์โทรศัพท์ (มีตัวเลข 9-10 หลัก) ให้ไปหน้าจัดการลูกค้า
    const cleanNum = q.replace(/\D/g, "");
    if (cleanNum.length >= 9 && cleanNum.length <= 10) {
      navigate(`/users?search=${cleanNum}`);
      return;
    }

    // นอกนั้นให้ลองเปิดหน้า Order
    navigate(`/orders/${q}`);
  };

  // If not dev, render the original minimal view
  if (!isDev) {
    return (
      <div className="w-full h-full flex flex-col justify-center items-center gap-3 select-none">
        <img
          src="/logo.png"
          alt="วันดีลอตเตอรี่"
          className="w-24 h-auto object-contain drop-shadow-xs grayscale-[100%] opacity-10"
          onError={(e) => {
            e.target.src = "/images/HT_new_logo.png";
          }}
        />
        <p className="text-slate-300 font-semibold text-xs">
          บริษัท วันดี ออนไลน์ จำกัด
        </p>
      </div>
    );
  }

  const devName =
    profileState?.nickname ||
    profileState?.firstName ||
    profileState?.displayName ||
    "Developer";

  const SHORTCUTS = [
    {
      title: "Admin Center",
      desc: "ศูนย์บัญชาการสด Live Carts & Security Monitor",
      path: "/admin-center",
      icon: <Activity className="w-5 h-5 text-blue" />,
      tag: "Live",
      tagColor: "bg-rose-50 text-rose-600 border-rose-200",
    },
    {
      title: "คำขอย้ายคำสั่งซื้อ",
      desc: "อนุมัติการย้ายสิทธิ์สลาก (Two-Person Approval)",
      path: "/orders/transfer-requests",
      icon: <ArrowRightLeft className="w-5 h-5 text-amber-600" />,
      badge: pendingTransfers.length,
    },
    {
      title: "จัดการสิทธิ์แอดมิน",
      desc: "กำหนด Master Roles & Custom Permissions",
      path: "/admins",
      icon: <Shield className="w-5 h-5 text-emerald-600" />,
    },
    // {
    //   title: "สลากที่มีปัญหา",
    //   desc: "จัดการบาร์โค้ดไม่ตรงและสลากซ้ำ",
    //   path: "/lotteries/unimported",
    //   icon: <AlertTriangle className="w-5 h-5 text-orange-600" />,
    // },
    {
      title: "Realtime Dashboard",
      desc: "แดชบอร์ดแสดงผลยอดขายแบบ TV Mode",
      path: "/realtime-dashboard",
      icon: <Tv className="w-5 h-5 text-indigo-600" />,
    },
    {
      title: "คำสั่งซื้อทั้งหมด",
      desc: "ตรวจสอบรายการออเดอร์และสถานะการจ่ายเงิน",
      path: "/orders",
      icon: <ShoppingBag className="w-5 h-5 text-teal-600" />,
    },
    {
      title: "จัดการข้อมูลลูกค้า",
      desc: "ค้นหา ดูข้อมูลโปรไฟล์ และเข้าสู่ระบบแทน",
      path: "/users",
      icon: <Users className="w-5 h-5 text-purple-600" />,
      badge: pendingAnonymizeCount > 0 ? `${pendingAnonymizeCount} รอเซนเซอร์` : null,
    },
    {
      title: "คลังสลากในระบบ",
      desc: "จัดการสลาก งวดสลาก และบล็อกสลาก",
      path: "/lotteries/manage",
      icon: <Ticket className="w-5 h-5 text-cyan-600" />,
    },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto py-8 px-4 sm:px-6 min-w-0 space-y-6 animate-in fade-in-50 duration-200">
      {/* Dev Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-slate-900 text-white rounded-md shadow-xs">
            <Code2 className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-800">
                Dev Cockpit &bull; {devName}
              </h1>
              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                Dev Mode
              </span>
            </div>

          </div>
        </div>

        {/* Quick Env Tag & Refresh */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="p-1.5 text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded-md border border-slate-200 transition-all text-xs flex items-center gap-1 font-semibold cursor-pointer shadow-2xs"
            title="รีเฟรชหน้าเว็บ"
          >
            <RotateCw className="w-3.5 h-3.5 text-slate-500" />
            <span className="hidden sm:inline text-[11px]">รีเฟรช</span>
          </button>
          <span className="text-xs text-slate-500 font-mono bg-slate-100 px-2.5 py-1 rounded-md border border-slate-200">
            Node: {process.env.NODE_ENV || "development"}
          </span>
        </div>
      </div>

      {/* Omni Quick Search Bar */}
      <div className="bg-white p-4 rounded-md border border-slate-200 shadow-xs">
        <form onSubmit={handleQuickSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
            <input
              type="text"
              placeholder="ค้นหาด่วน: พิมพ์รหัสคำสั่งซื้อ (Order ID) หรือ เบอร์โทรลูกค้า แล้วกด Enter..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 pl-10 pr-4 text-sm font-medium text-slate-800 bg-slate-50 border border-slate-300 rounded-md focus:outline-none focus:border-blue focus:bg-white transition"
            />
          </div>
          <button
            type="submit"
            className="h-11 px-5 bg-navy hover:bg-navy/90 text-white text-sm font-bold rounded-md shadow-xs flex items-center gap-2 transition cursor-pointer shrink-0"
          >
            <span>ค้นหา</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* Pending Transfers Alert (ถ้ามีเคสรออยู่) */}
      {pendingTransfers.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-md shadow-2xs space-y-3 animate-in fade-in-50">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200/80 pb-2">
            <div className="flex items-center gap-2">
              <span className="p-1.5 bg-amber-500 text-white rounded-md">
                <Clock className="w-4 h-4 animate-spin" />
              </span>
              <h3 className="text-sm font-bold text-amber-950">
                มีคำขอย้ายคำสั่งซื้อสลากรอ Dev อนุมัติ ({pendingTransfers.length} รายการ)
              </h3>
            </div>
            <button
              type="button"
              onClick={() => navigate("/orders/transfer-requests")}
              className="text-xs font-bold text-amber-800 hover:text-amber-950 flex items-center gap-1 cursor-pointer"
            >
              <span>เปิดดูทั้งหมดในระบบ</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {pendingTransfers.slice(0, 3).map((req) => (
              <div
                key={req.id}
                onClick={() => setSelectedTransfer(req)}
                className="bg-white p-3 rounded-md border border-amber-200 shadow-2xs hover:border-amber-400 hover:shadow-xs transition cursor-pointer flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono font-bold text-blue">
                      {req.shortId ? `#${req.shortId}` : req.orderId}
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">
                      {req.createdAt ? new Date(req.createdAt).toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                  <p className="text-xs font-bold text-slate-800 mt-1 truncate">
                    ไปยัง: {req.toUser?.firstName || req.toUser?.phone || "ลูกค้าปลายทาง"}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate mt-0.5">
                    เหตุผล: {req.reason}
                  </p>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
                  <span className="text-slate-400">ขอโดย: {req.requestedBy?.name || "แอดมิน"}</span>
                  <span className="font-bold text-amber-700 hover:underline">
                    คลิกตรวจ ➡️
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending User Anonymization Alert (ถ้ามียูสเซอร์ Removed รอเซนเซอร์) */}
      {pendingAnonymizeCount > 0 && (
        <div className="p-4 bg-rose-50 border border-rose-300 rounded-md shadow-2xs flex flex-wrap items-center justify-between gap-3 animate-in fade-in-50">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-rose-600 text-white rounded-md shadow-xs">
              <ShieldAlert className="w-5 h-5" />
            </span>
            <div>
              <h3 className="text-sm font-bold text-rose-950">
                มียูสเซอร์สถานะ Removed รอการเซนเซอร์ข้อมูลส่วนบุคคล ({pendingAnonymizeCount} บัญชี)
              </h3>
              <p className="text-xs text-rose-700 mt-0.5">
                แอดมินได้ทำการเปลี่ยนสถานะเป็น Removed แล้ว สามารถกดกวาดเซนเซอร์ข้อมูล PII ให้เป็นไปตามกฎหมายได้ทันที
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleOpenBatchModal}
            className="py-2.5 px-4 bg-rose-600 hover:bg-rose-700 active:opacity-95 text-white text-xs font-bold rounded-md shadow-xs transition flex items-center gap-2 cursor-pointer shrink-0"
          >
            <ShieldAlert className="w-4 h-4" />
            <span>เซนเซอร์ข้อมูลทั้งหมด ({pendingAnonymizeCount})</span>
          </button>
        </div>
      )}

      {/* Grid of Shortcuts */}
      <div className="space-y-2.5">
        <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5 text-blue" />
          <span>ทางลัดเข้าระบบด่วน (Quick Navigation)</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SHORTCUTS.map((item) => (
            <div
              key={item.path}
              onClick={() => navigate(item.path)}
              className="bg-white p-4 rounded-md border border-slate-200 shadow-2xs hover:shadow-md hover:border-blue transition-all cursor-pointer group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-slate-50 group-hover:bg-blue/10 rounded-md transition-colors">
                    {item.icon}
                  </div>
                  {item.tag && (
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md border ${item.tagColor}`}>
                      {item.tag}
                    </span>
                  )}
                  {typeof item.badge === "number" && item.badge > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-extrabold rounded-md bg-rose-500 text-white animate-pulse">
                      {item.badge} รออนุมัติ
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-sm text-slate-850 mt-3 group-hover:text-blue transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                  {item.desc}
                </p>
              </div>

              <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-slate-400 group-hover:text-blue transition-colors">
                <span>เปิดระบบ</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security & Order Transfer Audit Widget */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider">
            <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />
            <span>บันทึกความปลอดภัย & คำขอย้ายคำสั่งซื้อ (Security & Transfer Audit)</span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/logs")}
            className="text-xs font-bold text-navy hover:underline flex items-center gap-1"
          >
            <span>ดูบันทึกทั้งหมด</span>
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        <div className="bg-white rounded-md border border-slate-200 shadow-2xs overflow-hidden">
          {devLogs.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400 font-medium">
              {isLoadingLogs
                ? "กำลังโหลดข้อมูลความปลอดภัยและคำขอย้ายสลาก..."
                : "ยังไม่มีประวัติปัญหาความปลอดภัยหรือคำขอย้ายคำสั่งซื้อในช่วงนี้"}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {devLogs.map((log, idx) => {
                const isSecurity = log.type === "security";
                const isTransfer = log.type === "order_transfer";

                const actionLabel =
                  log.action === "admin_login_lockout"
                    ? "ระงับการเข้าสู่ระบบ Support (รหัสผ่านผิดเกินกำหนด)"
                    : log.action === "customer_bruteforce_detected"
                    ? "ตรวจพบการสุ่มรหัสผ่านลูกค้าผิดปกติ"
                    : log.action === "transfer_requested"
                    ? "ยื่นคำขอย้ายเจ้าของคำสั่งซื้อ"
                    : log.action === "transfer_approved"
                    ? "อนุมัติย้ายสิทธิ์คำสั่งซื้อเรียบร้อย"
                    : log.action === "transfer_rejected"
                    ? "ปฏิเสธคำขอย้ายคำสั่งซื้อ"
                    : log.action || log.type;

                return (
                  <div
                    key={log.id || idx}
                    className="p-3 hover:bg-slate-50/80 transition-colors flex items-center justify-between gap-3 text-xs"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {isSecurity && (
                        <span className="shrink-0 px-2 py-0.5 rounded-md font-bold text-[10px] bg-rose-100 text-rose-700 border border-rose-200">
                          🚨 SECURITY
                        </span>
                      )}
                      {isTransfer && (
                        <span className="shrink-0 px-2 py-0.5 rounded-md font-bold text-[10px] bg-amber-50 text-amber-800 border border-amber-200">
                          🔄 TRANSFER
                        </span>
                      )}
                      {!isSecurity && !isTransfer && (
                        <span className="shrink-0 px-2 py-0.5 rounded-md font-bold text-[10px] bg-navy text-white">
                          DEV
                        </span>
                      )}

                      <div className="min-w-0">
                        <p className="font-bold text-slate-800 truncate">
                          {actionLabel}
                          {log.target && (
                            <span className="font-medium text-slate-400 ml-1">
                              (Ref: {log.target})
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          {log.name || log.actor ? (
                            <>
                              โดย: <span className="text-slate-600 font-semibold">{log.name || log.actor}</span>
                            </>
                          ) : null}
                          {log.note && ` • ${log.note}`}
                        </p>
                      </div>
                    </div>

                    <div className="shrink-0 text-right text-[11px] text-slate-400 font-medium">
                      {log.createdAt ? (
                        <span>
                          {new Date(log.createdAt).toLocaleTimeString("th-TH", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}{" "}
                          น.
                        </span>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Review Modal if opened from home */}
      {selectedTransfer && (
        <TransferReviewModal
          isOpen={Boolean(selectedTransfer)}
          onClose={() => setSelectedTransfer(null)}
          request={selectedTransfer}
          onSuccess={() => {
            setSelectedTransfer(null);
          }}
        />
      )}

      {/* Batch Anonymize Modal */}
      <BatchAnonymizeModal
        isOpen={isBatchModalOpen}
        onClose={() => {
          setIsBatchModalOpen(false);
          fetchPendingAnonymizeSummary();
          fetchDevLogs();
        }}
        totalPending={pendingAnonymizeCount}
        onSuccess={() => {
          fetchPendingAnonymizeSummary();
          fetchDevLogs();
        }}
      />
    </div>
  );
};

export default Home;
